import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type MojoAgentLayoutMode = 'overlay' | 'push';
export type MojoAgentPanelWidth = 'default' | 'wide';

export const MOJO_AGENT_PANEL_WIDTH_PX = 400;
const LAYOUT_STORAGE_KEY = 'moneymojo-mojo-agent-layout';
const WIDTH_STORAGE_KEY = 'moneymojo-mojo-agent-width';
const LG_MEDIA_QUERY = '(min-width: 1024px)';

function readIsLgViewport(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia(LG_MEDIA_QUERY).matches;
}

function useIsLgViewport(): boolean {
  const [isLg, setIsLg] = useState(readIsLgViewport);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(LG_MEDIA_QUERY);
    const sync = () => setIsLg(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return isLg;
}

function readStoredLayoutMode(): MojoAgentLayoutMode {
  if (typeof window === 'undefined') return 'overlay';
  const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
  return stored === 'push' ? 'push' : 'overlay';
}

function readStoredPanelWidth(): MojoAgentPanelWidth {
  if (typeof window === 'undefined') return 'default';
  const stored = window.localStorage.getItem(WIDTH_STORAGE_KEY);
  return stored === 'wide' ? 'wide' : 'default';
}

export interface MojoAgentContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  registerToggleRef: (element: HTMLElement | null) => void;
  layoutMode: MojoAgentLayoutMode;
  setLayoutMode: (mode: MojoAgentLayoutMode) => void;
  /** Overlay width preference — applied only when unpinned (overlay). */
  panelWidth: MojoAgentPanelWidth;
  setPanelWidth: (width: MojoAgentPanelWidth) => void;
  /** True when overlay + wide preference (desktop). */
  isWideOverlay: boolean;
  /** Pinned chat narrows the main column — use tablet nav pattern. */
  compactNavigation: boolean;
}

const MojoAgentContext = createContext<MojoAgentContextValue | undefined>(undefined);

export function MojoAgentProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [layoutMode, setLayoutModeState] = useState<MojoAgentLayoutMode>(readStoredLayoutMode);
  const [panelWidth, setPanelWidthState] = useState<MojoAgentPanelWidth>(readStoredPanelWidth);
  const isLgViewport = useIsLgViewport();
  const toggleRef = useRef<HTMLElement | null>(null);
  const pushLayoutActive = isOpen && layoutMode === 'push' && isLgViewport;
  const isWideOverlay =
    isOpen && layoutMode === 'overlay' && panelWidth === 'wide' && isLgViewport;

  useEffect(() => {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layoutMode);
  }, [layoutMode]);

  useEffect(() => {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, panelWidth);
  }, [panelWidth]);

  const registerToggleRef = useCallback((element: HTMLElement | null) => {
    toggleRef.current = element;
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    requestAnimationFrame(() => {
      toggleRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        requestAnimationFrame(() => {
          toggleRef.current?.focus({ preventScroll: true });
        });
      }
      return next;
    });
  }, []);

  const setLayoutMode = useCallback((mode: MojoAgentLayoutMode) => {
    setLayoutModeState(mode);
  }, []);

  const setPanelWidth = useCallback((width: MojoAgentPanelWidth) => {
    setPanelWidthState(width);
  }, []);

  return (
    <MojoAgentContext.Provider
      value={{
        isOpen,
        open,
        close,
        toggle,
        registerToggleRef,
        layoutMode,
        setLayoutMode,
        panelWidth,
        setPanelWidth,
        isWideOverlay,
        compactNavigation: pushLayoutActive,
      }}
    >
      {children}
    </MojoAgentContext.Provider>
  );
}

export function useMojoAgent(): MojoAgentContextValue {
  const context = useContext(MojoAgentContext);
  if (context === undefined) {
    throw new Error('useMojoAgent must be used within a MojoAgentProvider');
  }
  return context;
}
